import {
  Page,
  CardTable,
  TableBottomCell,
  EtherscanLink,
  PageSkeleton
} from '@/components';

import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  CryptoIcon,
  CurrencyValue,
  Flex,
  TableSkeleton,
  WalletAddress,
  RelativeDateTime,
  DropdownFilter
} from '@blockanalitica/ui';
import { useApi, PaginatedResponse } from '@/api';
import { useParams } from 'react-router';
import { useNetwork } from '@/hooks';

type EventsData = {
  market_address: string;
  wallet_address: string;
  underlying_address: string;
  market_symbol: string;
  amount: string | null;
  event: string;
  event_symbol: string;
  usd_price_amount: string | null;
  order_index: string;
  datetime: string;
  tx_hash: string;
};

interface EventsResponse extends PaginatedResponse<EventsData[]> {}

const EVENT_TYPE_OPTIONS = [
  { label: 'All events', value: '' },
  { label: 'Withdraw', value: 'Withdraw' },
  { label: 'Supply', value: 'Supply' }
];

export default function EventsTable() {
  const network = useNetwork();
  const { marketAddress, walletAddress } = useParams();
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 20,
    initialSorting: '-order_index',
    initialFilters: { eventType: '' }
  });

  const [data, error] = useApi<EventsResponse>({
    url: `/markets/${marketAddress}/wallets/${walletAddress}/events/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      event_type: filters.filtersApplied.eventType
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <PageSkeleton>
        <TableSkeleton rows={10} />
      </PageSkeleton>
    );
  }
  const columns: TableColumn<EventsData>[] = [
    {
      header: 'Collateral Asset',
      getCell: (row) => (
        <CryptoIcon
          key={row.underlying_address}
          name={row.event_symbol}
          address={row.underlying_address}
          size="3"
          network={network}
        />
      )
    },
    {
      header: 'Event',
      getCell: (row) => row.event,
      sortingField: 'event',
      cellAlign: 'flex-start',
      headerAlign: 'flex-start'
    },
    {
      header: 'Amount',
      getCell: (row) => (
        <CurrencyValue
          value={row.usd_price_amount}
          options={{
            currency: {
              name: row.market_symbol,
              address: row.market_address,
              network: network
            },
            trend: true
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell checkValue={row.amount}>
          <CurrencyValue
            value={row.amount}
            options={{
              currency: {
                name: row.event_symbol,
                address: row.underlying_address,
                network: network
              }
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'amount',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs">
          <RelativeDateTime
            value={row.datetime}
            formatDateTimeOptions={{ toRelativeUnit: 'days' }}
          />
          <EtherscanLink value={row.tx_hash} network={network} />
        </Flex>
      ),

      sortingField: 'order_index',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <Page
      title={
        <WalletAddress
          options={{
            name: 'Wallet Activity',
            showCopyBtn: true,
            showEthScanLink: true,
            showDebankLink: true,
            sizes: {
              blockie: '6'
            }
          }}
          address={data.extra?.wallet_address}
        />
      }
      actions={
        <DropdownFilter
          filterField="eventType"
          options={EVENT_TYPE_OPTIONS}
          pagination={pagination}
          {...filters}
        />
      }>
      <CardTable<EventsData>
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.order_index}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
      />
    </Page>
  );
}
